/**
 * Every string the tag surface renders.
 *
 * The copy addresses the operator, never the reviewer: no design rationale, no
 * technical vocabulary — «many-to-many» is barred from the interface outright —
 * and no restatement of a mechanic the screen already performs. What is left
 * states the two things the operator cannot see: that a rename here reaches
 * every title already carrying the tag, and that a deletion lifts the tag off
 * those titles rather than removing them.
 */
export const copy = {
  crumbs: {
    root: 'لوحة التشغيل',
    current: 'الوسوم',
  },

  head: {
    title: 'الوسوم',
    lede: 'مفردات الوسوم التي تُلصَق بالعناوين. الوسم يجمع عناوين ولا يصنّفها: للعنوان الواحد ما شاء منها، ولكلّ وسمٍ صفحةٌ تعرض ما يحمله، ويظهر في شريط الوسوم فوق الفهرس.',
  },

  section: {
    heading: 'مفردات الوسوم',
    add: 'أضف وسماً',
    context:
      'الاسم واحدٌ لا نسخة لكلّ عنوان: تغييره هنا يسري في الحال على كلّ عنوان يحمل الوسم وعلى صفحته وعلى شريط الوسوم. وحذف الوسم يرفعه عن العناوين ولا يحذفها. أمّا الشجرة التي تُصنَّف بها العناوين فصفحتها «التصنيفات».',
    caption: 'مفردات الوسوم، وقائمة أفعال لكلّ مفردة',
  },

  columns: {
    name: 'الوسم',
    /** Visually hidden — the column holds a handle, and a handle has no name. */
    menu: 'أفعال الوسم',
  },

  /**
   * No catalogue row grants this surface a traversal control, so it states how
   * many rows it shows against how many exist — **always**, including when the
   * two figures are equal. Silent truncation is what is forbidden, not
   * truncation.
   */
  truncation: (shown: string, total: string) => `المعروض ${shown} من ${total} وسماً.`,

  menu: {
    label: (name: string) => `أفعال وسم «${name}»`,
    edit: 'حرّر الوسم',
    delete: 'احذف الوسم',
  },

  dialog: {
    /**
     * «أضف وسماً» rather than the demo script's «أضف» + «الوسم»: a definite
     * article on a record that does not exist yet, and the trigger beside it
     * already says the indefinite form.
     */
    titleCreate: 'أضف وسماً',
    titleEdit: 'حرّر الوسم',
    context: 'مفردات الوسوم',
    nameLabel: 'اسم الوسم',
    nameHint: 'يقرأه الزائر كما يُكتب هنا. تغييره يسري على كلّ عنوان يحمل الوسم، لا على هذه الصفحة وحدها.',
    save: 'احفظ',
  },

  validation: {
    nameRequired: 'اكتب اسم الوسم.',
    nameTooLong: 'الاسم أطول ممّا يقبله الحقل. اختصره.',
  },

  confirm: {
    heading: (name: string) => `حذف وسم «${name}» نهائياً؟`,
    body: 'يختفي الوسم من شريط الوسوم ومن صفحته، ويُرفع عن كلّ عنوان يحمله. العناوين نفسها تبقى في الفهرس كما هي.',
    commit: 'احذف الوسم',
  },

  announce: {
    created: (name: string) => `أُضيف الوسم «${name}».`,
    updated: (name: string) => `حُفظ الوسم «${name}».`,
    deleted: (name: string) => `حُذف الوسم «${name}».`,
  },

  states: {
    loading: 'جارٍ تحميل مفردات الوسوم',
    empty: 'لا وسوم بعد. أضف أوّل وسم لتبدأ به وسم العناوين.',
  },
} as const;
